import { useState, useEffect } from "react";
import API, { formatWIB } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Trash, ArrowCounterClockwise, CaretLeft, CaretRight, CheckSquare, Square, Warning } from "@phosphor-icons/react";

export default function TrashPage() {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const perPage = 20;

  // Selection
  const [selected, setSelected] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);

  useEffect(() => { loadTrash(); }, [page]);

  const loadTrash = async () => {
    setLoading(true);
    try {
      const skip = (page - 1) * perPage;
      const { data } = await API.get(`/jobs/trash/list?limit=${perPage}&skip=${skip}`);
      setJobs(data.jobs);
      setTotal(data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleRestore = async (jobId) => {
    try {
      await API.post(`/jobs/trash/restore/${jobId}`);
      loadTrash();
    } catch (e) { alert(e.response?.data?.detail || "Gagal mengembalikan"); }
  };

  const handlePermanentDelete = async (jobId) => {
    if (!window.confirm("HAPUS PERMANEN job ini? Data tidak bisa dikembalikan!")) return;
    try {
      await API.delete(`/jobs/trash/${jobId}/permanent`);
      loadTrash();
    } catch (e) { alert(e.response?.data?.detail || "Gagal menghapus"); }
  };

  const handleBulkPermanentDelete = async () => {
    if (deleteConfirmText !== "HAPUS PERMANEN") return;
    setDeleting(true);
    try {
      await API.post("/jobs/trash/bulk-permanent-delete", { job_ids: [...selected] });
      setSelected(new Set());
      setShowDeleteConfirm(false);
      setDeleteConfirmText("");
      loadTrash();
    } catch (e) { alert(e.response?.data?.detail || "Gagal menghapus"); }
    finally { setDeleting(false); }
  };

  const handleAutoCleanup = async () => {
    setCleaningUp(true);
    try {
      const { data } = await API.post("/jobs/trash/auto-cleanup");
      alert(`${data.count} job expired dihapus permanen`);
      loadTrash();
    } catch (e) { alert(e.response?.data?.detail || "Gagal cleanup"); }
    finally { setCleaningUp(false); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === jobs.length) setSelected(new Set());
    else setSelected(new Set(jobs.map(j => j.id)));
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div data-testid="trash-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 font-heading">Trash</h1>
          <p className="text-sm text-zinc-500 mt-1">{total} job di trash (auto-hapus setelah 7 hari)</p>
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <Button data-testid="bulk-permanent-delete-btn" variant="outline" onClick={() => { setShowDeleteConfirm(true); setDeleteConfirmText(""); }}
              className="rounded-sm gap-2 text-red-600 border-red-300 hover:bg-red-50 text-xs">
              <Trash size={14} /> Hapus Permanen {selected.size}
            </Button>
          )}
          <Button data-testid="auto-cleanup-btn" variant="outline" onClick={handleAutoCleanup} disabled={cleaningUp}
            className="rounded-sm gap-2 text-zinc-600 text-xs">
            <Trash size={14} /> {cleaningUp ? "..." : "Cleanup Expired"}
          </Button>
        </div>
      </div>

      {total === 0 && !loading ? (
        <div className="border border-zinc-200 rounded-sm p-12 text-center">
          <Trash size={32} className="text-zinc-300 mx-auto mb-3" />
          <p className="text-sm text-zinc-400">Trash kosong</p>
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="px-2 py-2 w-8">
                    <button data-testid="trash-select-all" onClick={toggleSelectAll} className="text-zinc-400 hover:text-zinc-900">
                      {selected.size > 0 && selected.size === jobs.length ? <CheckSquare size={16} weight="fill" className="text-zinc-900" /> : <Square size={16} />}
                    </button>
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">ID</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Tanggal Data</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Status</th>
                  <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Records</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Dihapus Oleh</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Dihapus</th>
                  <th className="px-3 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Sisa Hari</th>
                  <th className="px-3 py-2 w-24" />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-zinc-400">Loading...</td></tr>
                ) : jobs.map((job) => (
                  <tr key={job.id} className={`border-b border-zinc-100 transition-colors ${selected.has(job.id) ? "bg-red-50" : "hover:bg-zinc-50"}`}>
                    <td className="px-2 py-2">
                      <button onClick={() => toggleSelect(job.id)} className="text-zinc-400 hover:text-zinc-900">
                        {selected.has(job.id) ? <CheckSquare size={16} weight="fill" className="text-zinc-900" /> : <Square size={16} />}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono text-zinc-900">{job.id?.slice(0, 8)}</td>
                    <td className="px-3 py-2 text-xs font-mono text-zinc-600">{job.date_from} ~ {job.date_to}</td>
                    <td className="px-3 py-2"><StatusBadge status={job.status} /></td>
                    <td className="px-3 py-2 text-xs font-mono text-right">{job.total_records}</td>
                    <td className="px-3 py-2 text-xs text-zinc-500">{job.deleted_by || "-"}</td>
                    <td className="px-3 py-2 text-[10px] font-mono text-zinc-500">{formatWIB(job.deleted_at)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-xs font-mono font-semibold ${job.auto_delete_in <= 1 ? "text-red-600" : job.auto_delete_in <= 3 ? "text-amber-600" : "text-zinc-500"}`}>
                        {job.auto_delete_in}d
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex gap-1">
                        <button data-testid={`restore-job-${job.id?.slice(0,8)}`} onClick={() => handleRestore(job.id)}
                          className="text-emerald-500 hover:text-emerald-700 transition-colors p-1" title="Kembalikan">
                          <ArrowCounterClockwise size={14} />
                        </button>
                        <button data-testid={`perm-delete-${job.id?.slice(0,8)}`} onClick={() => handlePermanentDelete(job.id)}
                          className="text-red-400 hover:text-red-600 transition-colors p-1" title="Hapus Permanen">
                          <Trash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between">
            <p className="text-xs text-zinc-500 font-mono">
              {total > 0 ? `${(page-1)*perPage + 1}-${Math.min(page*perPage, total)} dari ${total}` : "0 jobs"}
            </p>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="rounded-sm h-7 w-7 p-0"><CaretLeft size={14} /></Button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <Button key={pageNum} variant={page === pageNum ? "default" : "outline"} size="sm"
                    onClick={() => setPage(pageNum)}
                    className={`rounded-sm h-7 w-7 p-0 text-xs ${page === pageNum ? "bg-zinc-900 text-white" : ""}`}>
                    {pageNum}
                  </Button>
                );
              })}
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="rounded-sm h-7 w-7 p-0"><CaretRight size={14} /></Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Permanent Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-red-700">Hapus Permanen</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-red-50 border border-red-200 rounded-sm p-3 flex gap-2">
              <Warning size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-red-800">
                <p className="font-semibold">PERINGATAN: Tindakan ini TIDAK BISA dibatalkan!</p>
                <p className="mt-1">{selected.size} job beserta semua data terkait (records, match candidates, outflows, progress) akan dihapus permanen.</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-zinc-600">Ketik <span className="font-bold text-red-600">HAPUS PERMANEN</span> untuk konfirmasi:</Label>
              <Input data-testid="perm-delete-confirm-input" value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Ketik HAPUS PERMANEN" className="rounded-sm border-zinc-300 font-mono" autoFocus />
            </div>
            <div className="flex gap-2">
              <Button data-testid="confirm-perm-delete" onClick={handleBulkPermanentDelete} disabled={deleteConfirmText !== "HAPUS PERMANEN" || deleting}
                className="flex-1 bg-red-600 text-white hover:bg-red-700 rounded-sm">
                {deleting ? "Menghapus..." : "Hapus Permanen"}
              </Button>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="rounded-sm">Batal</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
